import { useParams, useNavigate } from "react-router-dom";
import { UserEditForm } from "@/components/users";
import { LoadingSpinner } from "@/components/common/LoadingSpinner";
import { ErrorFallback } from "@/components/common/ErrorFallback";
import { PageHeader } from "@/components/common/page-header";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { AlertTriangle } from "lucide-react";
import { useUserDetail } from "@/hooks/useUserQueries";

export function UserEditPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const {
    data: user,
    isLoading,
    isFetching,
    error,
    refetch,
  } = useUserDetail(id!);

  if (isLoading) {
    return (
      <div className="flex h-96 items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  // Full-screen only when there is nothing to edit at all — see UserDetailPage for the same fix
  // and the reason: a failed background refetch must not throw away a user still in the cache.
  if (!user) {
    return (
      <div className="space-y-6">
        <PageHeader
          title="Edit User"
          subtitle="Update user information and permissions"
          back={{
            label: "Back to Users",
            onClick: () => navigate("/admin/users"),
          }}
        />
        <ErrorFallback error={error as Error} onRetry={() => refetch()} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Edit User"
        subtitle="Update user information and permissions"
        back={{
          label: "Back to User",
          onClick: () => navigate(`/admin/users/${user.id}`),
        }}
      />

      {error && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Could not refresh user details</AlertTitle>
          <AlertDescription className="space-y-2">
            <p>The form below may be showing a stale copy of this user.</p>
            <Button
              size="sm"
              variant="outline"
              onClick={() => refetch()}
              disabled={isFetching}
            >
              Retry
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {/* Form */}
      <div className="max-w-2xl">
        <UserEditForm user={user} />
      </div>
    </div>
  );
}
